import { useEffect, useMemo, useRef, useState } from 'react'
import {
  CheckboxFilterTree,
  type CheckedStateRecord,
  type FilterItemProps
} from '@jana-technology/platform-ui'

import { IEventTableMeta } from 'app/services/api/datamanagement/eventTableAttributes'
import { SelectDropdownWrapper } from 'common/components/SelectDropdownWrapper'

type EventTableOption = FilterItemProps

type CategoryOptionsMap = Record<string, EventTableOption[]>

interface AttributeSetSelectorProps {
  eventTables: IEventTableMeta[]
  selectedValues: string[]
  onChange: (selection: string[]) => void
}

const deriveCheckedState = (
  options: EventTableOption[],
  selectedValues: string[]
): CheckedStateRecord => {
  const selectedValueSet = new Set(selectedValues)
  const checkedState: CheckedStateRecord = {}

  const visit = (option: EventTableOption): boolean | 'indeterminate' => {
    if (!option.children?.length) {
      const checked = selectedValueSet.has(option.value)

      checkedState[option.value] = checked

      return checked
    }

    const childStates = option.children.map(visit)
    let checked: boolean | 'indeterminate' = false

    if (childStates.every((state) => state === true)) {
      checked = true
    } else if (childStates.some(Boolean)) {
      checked = 'indeterminate'
    }

    checkedState[option.value] = checked

    return checked
  }

  options.forEach(visit)

  return checkedState
}

const getSelectedLeafValues = (
  options: EventTableOption[],
  checkedState: CheckedStateRecord
): string[] =>
  options.flatMap((option) => {
    if (option.children?.length) {
      return getSelectedLeafValues(option.children, checkedState)
    }

    return checkedState[option.value] === true ? [option.value] : []
  })

const filterTreeOptions = (
  options: EventTableOption[],
  searchValue: string
): EventTableOption[] => {
  const normalizedSearchValue = searchValue.toLowerCase()

  return options.flatMap((option) => {
    if (option.label.toLowerCase().includes(normalizedSearchValue)) {
      return [option]
    }

    const children = option.children
      ? filterTreeOptions(option.children, searchValue)
      : []

    return children.length ? [{ ...option, children }] : []
  })
}

const getExpandableValues = (options: EventTableOption[]): string[] =>
  options.flatMap((option) =>
    option.children?.length
      ? [option.value, ...getExpandableValues(option.children)]
      : []
  )

const getSelectedAncestorValues = (
  options: EventTableOption[],
  selectedValues: string[]
): string[] => {
  const selectedValueSet = new Set(selectedValues)
  const expandedValues: string[] = []

  const containsSelectedValue = (option: EventTableOption): boolean => {
    if (!option.children?.length) {
      return selectedValueSet.has(option.value)
    }

    const hasSelectedDescendant = option.children.some(containsSelectedValue)

    if (hasSelectedDescendant) {
      expandedValues.push(option.value)
    }

    return hasSelectedDescendant
  }

  options.forEach(containsSelectedValue)

  return expandedValues
}

const AttributeSetSelector = ({
  eventTables,
  selectedValues,
  onChange
}: AttributeSetSelectorProps) => {
  const [searchValue, setSearchValue] = useState('')
  const filterTreeRef = useRef<HTMLDivElement>(null)

  const eventTableOptions = useMemo(() => {
    const categoriesAndOptions: CategoryOptionsMap = eventTables.reduce(
      (eventTablesMap, eventTable) => {
        const { categoryName } = eventTable.dataManagementCategory
        const category =
          eventTablesMap[categoryName] || (eventTablesMap[categoryName] = [])

        category.push({
          label: eventTable.name,
          value: eventTable.name
        })

        return eventTablesMap
      },
      Object.create(null)
    )

    return Object.keys(categoriesAndOptions)
      .sort((left, right) => left.localeCompare(right))
      .map((categoryName) => ({
        label: categoryName,
        value: categoryName,
        children: [...categoriesAndOptions[categoryName]].sort((left, right) =>
          left.value.localeCompare(right.value)
        )
      }))
  }, [eventTables])

  const searchOptions = useMemo(
    () =>
      eventTables.map((eventTable) => ({
        label: eventTable.name,
        value: eventTable.name
      })),
    [eventTables]
  )

  const filteredEventTableOptions = useMemo(
    () => filterTreeOptions(eventTableOptions, searchValue),
    [eventTableOptions, searchValue]
  )

  const checkedState = useMemo(
    () => deriveCheckedState(eventTableOptions, selectedValues),
    [eventTableOptions, selectedValues]
  )

  const defaultExpandedValues = useMemo(
    () =>
      Array.from(
        new Set([
          ...(searchValue
            ? getExpandableValues(filteredEventTableOptions)
            : []),
          ...getSelectedAncestorValues(eventTableOptions, selectedValues)
        ])
      ),
    [eventTableOptions, filteredEventTableOptions, searchValue, selectedValues]
  )

  useEffect(() => {
    if (!searchValue) {
      return
    }

    filterTreeRef.current
      ?.querySelectorAll<HTMLButtonElement>('button[aria-expanded="false"]')
      .forEach((trigger) => trigger.click())
  }, [filteredEventTableOptions, searchValue])

  const validValues = useMemo(
    () => new Set(searchOptions.map((option) => option.value)),
    [searchOptions]
  )

  return (
    <SelectDropdownWrapper
      mode='tags'
      className='w-full'
      contentClassName='max-h-80 overflow-y-auto'
      options={searchOptions}
      value={selectedValues}
      onChange={(value) => {
        const values = Array.isArray(value) ? value : [value]

        onChange(values.filter((item) => validValues.has(item)))
      }}
      onSearch={setSearchValue}
      placeholder='Please select'
      tagGroupMaxWidth={350}
    >
      <div ref={filterTreeRef}>
        <CheckboxFilterTree
          filters={filteredEventTableOptions}
          fullFilterTree={eventTableOptions}
          defaultExpandedValues={defaultExpandedValues}
          checkedState={checkedState}
          onCheckedChange={(nextCheckedState) =>
            onChange(getSelectedLeafValues(eventTableOptions, nextCheckedState))
          }
        />
      </div>
    </SelectDropdownWrapper>
  )
}

export default AttributeSetSelector
